import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { DatasetContextType } from '../../../types/context';
import { Dataset, GlobalLocationStateType } from '../../../types/types';

interface DatasetInitialFetchProps {
  dataset: Dataset | undefined;
  datasetId: string | undefined;
  data: unknown[] | undefined;
  datasetData: { datasetId?: string } | undefined;
  dataHookLoadingDatasetId: string | undefined;
  activeSorting: unknown[];
  activeFilters: Record<string, unknown> | null;
  fetchDatasetData: DatasetContextType['fetchDatasetData'];
}

export const useDatasetInitialFetch = ({
  dataset,
  datasetId,
  data,
  datasetData,
  dataHookLoadingDatasetId,
  activeSorting,
  activeFilters,
  fetchDatasetData,
}: DatasetInitialFetchProps) => {
  const { state } = useLocation<GlobalLocationStateType>();
  const quickBookmarkRestoreDatasetIdRef = useRef<string | undefined>(
    undefined
  );

  // The quick-bookmark filter is restored by DatasetContext. Do not let the
  // child list start an unfiltered request first; after the one-shot router
  // state is consumed, this effect reruns with the restored activeFilters.
  useEffect(() => {
    if (state?.quickBookmarkFilters !== undefined) {
      quickBookmarkRestoreDatasetIdRef.current = datasetId;
      return;
    }

    if (
      quickBookmarkRestoreDatasetIdRef.current !== undefined &&
      quickBookmarkRestoreDatasetIdRef.current !== datasetId
    ) {
      quickBookmarkRestoreDatasetIdRef.current = undefined;
    }

    const isQuickBookmarkRestore =
      quickBookmarkRestoreDatasetIdRef.current === datasetId &&
      activeFilters !== null;

    // DatasetContext owns filterFirst fetching. Do not carry this one-shot
    // exception into a later visit of the same non-filterFirst dataset.
    if (isQuickBookmarkRestore && dataset?.filterFirst) {
      quickBookmarkRestoreDatasetIdRef.current = undefined;
      return;
    }

    const shouldFetch =
      dataset &&
      dataset.datasetId === datasetId &&
      data === undefined &&
      (isQuickBookmarkRestore || datasetData?.datasetId !== datasetId) &&
      dataHookLoadingDatasetId !== datasetId &&
      !dataset.filterFirst;

    if (shouldFetch) {
      quickBookmarkRestoreDatasetIdRef.current = undefined;
      void fetchDatasetData(
        undefined,
        undefined,
        activeSorting,
        activeFilters || undefined
      );
    }
  }, [
    activeFilters,
    activeSorting,
    data,
    dataHookLoadingDatasetId,
    dataset,
    datasetData?.datasetId,
    datasetId,
    state?.quickBookmarkFilters,
  ]);
};
